import type { MouseEvent } from "react";
import { ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

// Vote counts on the public board run into the thousands; 1.2k keeps the
// control the same width no matter how popular a raise gets.
function formatCount(count: number): string {
  if (count < 1000) return String(count);
  const k = count / 1000;
  return `${k >= 10 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, "")}k`;
}

/**
 * ProductHunt-style upvote control. Filled when the current user has voted.
 *
 * `solid` is the in-product treatment. `outline` is the public Raiseboard
 * treatment: a quiet bordered control that sits on white cards without
 * competing with the primary action.
 */
export function UpvoteButton({
  count,
  voted,
  onClick,
  variant = "solid",
}: {
  count: number;
  voted: boolean;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  variant?: "solid" | "outline";
}) {
  const outline = variant === "outline";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={voted ? "Remove upvote" : "Upvote"}
      aria-pressed={voted}
      className={cn(
        "flex shrink-0 cursor-pointer flex-col items-center justify-center gap-0.5 border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
        outline ? "w-14 rounded-xl py-2.5" : "h-14 w-14 rounded-xl",
        voted
          ? "border-primary bg-primary text-primary-foreground"
          : outline
            ? "border-border bg-card text-foreground hover:border-primary/40 hover:bg-accent"
            : "border-primary/40 bg-primary/5 text-primary hover:bg-primary hover:text-primary-foreground"
      )}
    >
      <ChevronUp className="h-5 w-5" strokeWidth={2.5} />
      <span
        className={cn(
          "text-xs font-bold tabular-nums",
          outline ? "font-sans" : "font-mono"
        )}
      >
        {outline ? formatCount(count) : count}
      </span>
    </button>
  );
}
